import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Button } from '@components/Button';
import { useApplyToApplicationJob } from '@hooks/jobsBoard/useApplyToApplicationJob';
import { RootState } from '@lib/redux.lib/store';
import { getApplicationDeadlineInfo } from '@lib/utils/applicationHelper';

import { ApplicationSubmitModal } from '../ApplicationSubmitModal';

type JobPostApplySectionProps = {
  jobPost: JobsBoardApplication;
  className?: string;
};
export const JobPostApplySection = ({
  jobPost,
  className,
}: JobPostApplySectionProps) => {
  const {
    onApplyButtonClick,
    isApplying,
    applicationSubmitModalVisibility,
    publicApplicationData,
  } = useApplyToApplicationJob(
    jobPost?.application_link,
    jobPost?.application_status
  );

  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const { hasDeadline, formattedDeadline } = useMemo(
    () =>
      getApplicationDeadlineInfo(
        jobPost?.jobs_board_close_submissions_date_time,
        jobPost?.application_status,
        jobPost?.project_location?.country
      ),
    [
      jobPost?.jobs_board_close_submissions_date_time,
      jobPost?.application_status,
      jobPost?.project_location?.country,
    ]
  );

  const applyButtonText = useMemo(() => {
    if (jobPost?.application_status === 'open') {
      return 'Apply';
    }
    if (jobPost?.application_status === 'submitted') {
      return 'Submitted';
    }
    return 'Application Closed';
  }, [jobPost?.application_status]);

  const isApplyButtonDisabled = useMemo(() => {
    return jobPost?.application_status !== 'open' || isApplying;
  }, [jobPost?.application_status, isApplying]);

  return (
    <StyledContainer className={className}>
      <ApplicationSubmitModal
        applicationSubmitModalVisibility={applicationSubmitModalVisibility}
        jobPost={jobPost}
        loggedInUserId={loggedInUserId}
        publicApplicationData={publicApplicationData}
      />
      {hasDeadline && (
        <div className='deadline-container'>
          <div className='icon-container'>
            <InfoCircleOutlined />
          </div>
          <div className='deadline-text'>
            Closes on {formattedDeadline || '-'}
          </div>
        </div>
      )}
      <Button
        type='primary'
        className='apply-button'
        disabled={isApplyButtonDisabled}
        onClick={() => onApplyButtonClick()}
      >
        {applyButtonText}
      </Button>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .apply-button {
    width: 100%;
    height: 40px;
    border-radius: 5px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;

    @media (max-width: 768px) {
      height: 48px;
    }
  }

  .deadline-container {
    display: flex;
    align-items: center;
    gap: 8px;

    .icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;

      svg {
        width: 15px;
        height: auto;
        fill: ${({ theme }) => theme.palette.primary[0]};
      }
    }

    .deadline-text {
      font-size: 14px;
      line-height: 20px;
      width: calc(100% - 28px);
    }
  }
`;
